<template>
  <div
    :id="'p'+page.id+'-grid'"
    class="grid-stack"
  >
    <widget-container
      v-for="widget in widgets"
      :key="widget.id"
      :widget="widget"
    >
      <widget-menu
        v-if="!preview"
        :page-id="page.id"
        :widget="widget"
        @refresh="widget => {generateWidget(widget)}"
        @shrink="widget => {shrinkToFitPage(widget)}"
      />
    </widget-container>
  </div>
</template>

<script>
import _ from 'lodash';
import ApexCharts from 'apexcharts';
import axios from 'axios';
import moment from 'moment';
import {mapActions, mapMutations, mapState} from 'vuex';
import {GridStack} from 'gridstack';
import 'gridstack/dist/gridstack.min.css';
import 'gridstack/dist/h5/gridstack-dd-native';

import WidgetContainer from '../items/WidgetContainer.vue';
import WidgetMenu from '../items/WidgetMenu.vue';

export default {
  components: {
    WidgetContainer,
    WidgetMenu
  },
  props: {
    page: {
      type: Object,
      required: true
    },
    preview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      grid: null,
      compareRanges: {
        'custom': 'Previous Range',
        'previous_period': 'Previous Period',
        'previous_month': 'Previous Month',
        'previous_year': 'Previous Year'
      },
      gridRowCount: 100
    }
  },
  computed: {
    ...mapState('report', ['report']),
    ...mapState('pages', ['pages', 'dataType']),
    ...mapState('user', ['user']),
    widgets() {
      return this.page.layout;
    }
  },
  async mounted() {
    if (this.preview) {
      this.buildGrid();
    } else {
      this.scalePageSize().then(() => {
        this.buildGrid();
      });
    }
  },
  methods: {
    ...mapMutations({
      toggleEditing: 'pages/toggleEditing',
      updateLayout: 'pages/updateLayout'
    }),
    ...mapActions({
      addWidget: 'pages/addWidget',
      updateWidget: 'pages/updateWidget',
      deleteWidget: 'pages/deleteWidget',
      savePages: 'pages/savePages',
      savePagesForce: 'pages/savePagesRequest',
      scalePageSize: 'pages/scalePageSize',
      openModal: 'widgetModal/openModal'
    }),
    /**
     * Main function that builds the Gridstack grid, initialises all the widgets
     * and adds event listeners to each widget.
     *
     * @return  void
     */
    buildGrid() {
      // Calculate cell height for the grid
      let pageContent = window.getComputedStyle(document.querySelector(`#p${this.page.id} .page-content`));
      let contentHeight = parseInt(pageContent.getPropertyValue('height').slice(0, -2));
      let gridCellHeight = (Math.floor((contentHeight / this.gridRowCount) * 100) / 100) + 'px';

      // Initialise Gridstack grid
      this.grid = GridStack.init({
        float: true,
        acceptWidgets: true,
        resizable: {autoHide: true, handles: 'se,sw'},
        staticGrid: this.preview,
        cellHeight: gridCellHeight,
        minRow: this.gridRowCount,
        margin: '2px'
      }, 'p' + this.page.id + '-grid');

      // Loop through the widgets to update the elements with all of the widget data (attributes etc)
      this.widgets.forEach(async widget => {
        var widgetEl = document.querySelector(`[gs-id="${widget.id}"`);
        this.grid.update(widgetEl, widget);
        this.generateWidget(widget);
      });

      // Set Grid Events //

      // Dropped event occurs when widget is dropped onto a new grid/page
      this.grid.on('dropped', async (event, prevWidget, newWidget) => {
        const prevWidgetFull = _.find(this.grid.save(), {id: prevWidget.id});
        // As were using Vue components, we first need to delete the original widget
        this.toggleEditing(false);
        this.deleteWidget({
          widget: prevWidget,
          pageId: parseInt(prevWidget.grid.el.id.slice(1, prevWidget.grid.el.id.indexOf('-')))
        });
        // Then we prepare the widget object to be added as if it were a new widget
        let prepWidget = {
          id: prevWidgetFull.id,
          type: prevWidgetFull.type,
          attributes: prevWidgetFull.attributes,
          content: prevWidgetFull.content,
          h: newWidget.h,
          w: newWidget.w,
          x: newWidget.x,
          y: newWidget.y,
        };

        // Then add it to the page where its then created as a new widget component.
        await this.addWidget({widget: prepWidget, pageId: this.page.id});
      });

      this.grid.on('change', (event, widgets) => {
        if (!this.report.is_template) {
          widgets.forEach(widget => {
            this.generateWidget(widget); // why re-generate just because of resize/move?
          });
        }
        if (!this.preview) this.savePages();
      });

      // Toggle editing flag on pages if the widgets are in the process of being edited
      this.grid.on('resizestart', () => {
        document.querySelectorAll('.grid-stack').forEach(element => {
          element.style.border = 'solid 1px #cc3131';
        });
        document.querySelectorAll('.grid-stack-item-content').forEach(element => {
          element.style.border = 'solid 1px lightgrey';
        });
        this.toggleEditing(true);
      });
      this.grid.on('resizestop', () => {
        document.querySelectorAll('.grid-stack').forEach(element => {
          element.style.border = '';
        });
        document.querySelectorAll('.grid-stack-item-content').forEach(element => {
          element.style.border = 'solid 1px white';
        });
        this.toggleEditing(false);
      });

      /**
       * Add border on the grid to highlight the boundaries of the grid
       * whenever a widget is dragged. Remove border when dragging is
       * stopped or if the widget is removed from the grid.
       */

      this.grid.on('dragstart', () => {
        this.toggleEditing(true);
        document.querySelectorAll('.grid-stack').forEach(element => {
          element.style.border = 'solid 1px #cc3131';
        });
        document.querySelectorAll('.grid-stack-item-content').forEach(element => {
          element.style.border = 'solid 1px lightgrey';
        });
      });
      this.grid.on('dragstop', () => {
        this.toggleEditing(false);
        document.querySelectorAll('.grid-stack').forEach(element => {
          element.style.border = '';
        });
        document.querySelectorAll('.grid-stack-item-content').forEach(element => {
          element.style.border = 'solid 1px white';
        });
      });

      this.grid.on('removed', () => {
        // When a widget is removed, we must save the page.
        if (!this.preview) this.savePages();
        document.querySelectorAll('.grid-stack').forEach(element => {
          element.style.border = '';
        });

      });

      // End Grid Events //
    },

    /**
     * Global function for generating all Apex Charts
     *
     * @param   Widget        Widget object
     * @param   ChartOptions  Chart options to be used in Apex Chart
     * @param   Refresh       Whether we are refreshing the chart or generating new one
     *
     * @return  void
     */
    renderChart(widget, chartOptions) {
      var widgetContainer = document.querySelector(`#${widget.id}`);

      // If the chart is already rendered, we need to destroy it first
      if (!widgetContainer.children[0].classList.contains('widget-loading')) {
        try {
          ApexCharts.exec(widget.attributes.chartOptions.chart.id, 'destroy');
        } catch (error) {
          console.log(error, widgetContainer, widgetContainer.children.length);
        }
      }
      if (widgetContainer) {
        widgetContainer.innerHTML = '';
      } else {
        console.log('Widget container no longer in dom');
        return;
      }

      if (widget.type === 'insights') {
        chartOptions.title = {
          text: 'Insights data is no longer available from Google. Please replace this widget with a "Performance" widget',
          align: 'center',
          style: {
            color: '#F34F4F',
          }
        };
      }

      var chart = new ApexCharts(widgetContainer, chartOptions);
      chart.render();
    },

    /**
     * Global function for generating tables
     *
     * @param   widget     Widget object
     * @param   data       Dataset for the table
     *
     * @return  void
     */
    renderTable(widget, data) {
      try {
        let stringLimit = 500;
        // Get container and set its height to 100%
        const widgetContainer = document.querySelector(`#${widget.id}`);
        if (widgetContainer) {
          widgetContainer.innerHTML = '';
        } else {
          console.log('Widget container no longer in dom');
          return;
        }
        widgetContainer.style.height = '100%';

        // Create table element, apply class and height styling
        const table = document.createElement('div');
        table.classList.add('widget-table');
        table.style.setProperty('height', '100%');

        // Declare array to gather column sizes
        let columnSizes = [];
        // Loop through selected header/columns and build header row
        _.forEach(widget.attributes.options.visibleColumns, header => {
          const headerCell = document.createElement('span');
          headerCell.classList.add('widget-table-header');
          if (widget.attributes.tableOptions) {
            headerCell.style.justifyContent = widget.attributes.tableOptions.align;
            headerCell.style.backgroundColor = widget.attributes.tableOptions.headerColor;
            headerCell.style.color = widget.attributes.tableOptions.headerTextColor;
          }
          headerCell.innerHTML = `<strong>${data.headers[header].text}</strong>`;
          columnSizes.push(data.headers[header].size);
          table.appendChild(headerCell);
        });
        // Set column sizes using collected array of sizes
        table.style.gridTemplateColumns = columnSizes.join(' ');

        // Striped row flag
        let secondaryRow = true;
        if (data.data.length > 0) {
          // Loop through each row of data and build the cells, appending to the grid.
          //let previousRow = null;
          _.forEach(data.data, row => {
            _.forEach(widget.attributes.options.visibleColumns, header => {
              const rowCell = document.createElement('span');
              let skipRow = false;
              // Determine type of value and display in given format
              if (data.headers[header].isDate && row[header]) { // Date
                rowCell.innerHTML = moment(row[header]).format(this.report.date_format);
              } else if (data.headers[header].isRAG && row[header]) { // RAG
                if (row[header] >= 8) {
                  rowCell.classList.add('green')
                } else if (row[header] >= 5) {
                  rowCell.classList.add('amber');
                } else {
                  rowCell.classList.add('red');
                }
                rowCell.innerHTML = row[header].length > stringLimit ? `${row[header].substring(0, stringLimit)}...` : row[header];
              } else if (data.headers[header].isPriority && row[header]) { // Priority
                if (row[header] >= 8) {
                  rowCell.classList.add('high');
                  rowCell.innerHTML = 'High';
                } else if (row[header] >= 6) {
                  rowCell.classList.add('medium');
                  rowCell.innerHTML = 'Medium';
                } else {
                  rowCell.classList.add('low');
                  rowCell.innerHTML = 'Low';
                }
              } else if (data.headers[header].isKeywordDifference) {
                if (row[header] === null) {
                  rowCell.innerHTML = `<span class="tooltipster tooltipstered" title="The keyword was not found within the first 10 pages of Google">-</span>`
                } else {
                  // Get the difference between the current position and the previous position
                  if (row['current_position'] !== '100+' && row[header] === '100+') row[header] = 101;
                  if (row['current_position'] === '100+' && row[header] !== '100+') row['current_position'] = 101;
                  let difference = row['current_position'] - row[header];

                  const tooltipText = header === 'first_position' ?
                    `Your position on ${moment(row['first_date']).format(this.report.date_format)} was ${row[header]}` :
                    `Your position on this day was ${row[header]}`

                  if (difference > 0) {
                    rowCell.innerHTML = `<span class="text-danger tooltipster" title="${tooltipText}"><i class="fas fa-caret-down"></i> ${difference}</span>`
                  } else if (difference < 0) {
                    // Flip the value to positive or negative
                    difference = difference * -1
                    rowCell.innerHTML = `<span class="text-success tooltipster tooltipstered" title="${tooltipText}"><i class="fas fa-caret-up"></i> ${difference}</span>`
                  } else {
                    rowCell.innerHTML = header === 'first_position'
                      ? `<span class="tooltipster" title="${tooltipText}">${row[header]}</span>`
                      : `<span>${row[header]}</span>`;
                  }
                }

              } else if (data.headers[header].isSection && Object.keys(row).length === 2) {
                rowCell.innerHTML = `<strong>${row[header]}</strong>`;
              } else if (data.headers[header].fullRow && Object.keys(row).length === 2) {
                rowCell.innerHTML = `<strong>${row[header]}</strong>`;
                rowCell.style.gridColumn = '2 / ' + (Object.keys(widget.attributes.options.visibleColumns).length + 1);
                rowCell.style.borderLeft = 'none';
                skipRow = true;
              } else { // Default
                if (row[header] === null || row[header] === undefined || row[header] === 'null' || row[header] === 'undefined') {
                  rowCell.innerHTML = '-';
                } else {
                  rowCell.innerHTML = `<div>${row[header]}</div>`;
                }
              }
              if (header === 'difference') {
                if (row['class']) rowCell.classList.add(row['class']);
              }

              if (widget.attributes.tableOptions) {
                rowCell.style.justifyContent = widget.attributes.tableOptions.align;
                if (!secondaryRow) {
                  rowCell.style.backgroundColor = widget.attributes.tableOptions.primaryRowColor;
                  if (rowCell.classList.length === 0) {
                    rowCell.style.color = widget.attributes.tableOptions.primaryRowTextColor;
                  }
                } else {
                  rowCell.style.backgroundColor = widget.attributes.tableOptions.secondaryRowColor;
                  if (rowCell.classList.length === 0) {
                    rowCell.style.color = widget.attributes.tableOptions.secondaryRowTextColor;
                  }
                }
              } else {
                if (secondaryRow) rowCell.style.backgroundColor = '#F8F7F5';
              }

              table.appendChild(rowCell);
              // previousRow = row; TODO: why is this not used?
              if (skipRow) return false;
            });
            secondaryRow = !secondaryRow;
          });
        } else {
          _.forEach(widget.attributes.options.visibleColumns, () => {
            const rowCell = document.createElement('span');
            rowCell.innerHTML = 'No Data';
            if (widget.attributes.tableOptions) {
              rowCell.style.justifyContent = widget.attributes.tableOptions.align;
              rowCell.style.backgroundColor = widget.attributes.tableOptions.secondaryRowColor;
              rowCell.style.color = widget.attributes.tableOptions.secondaryRowTextColor;
            } else {
              rowCell.style.backgroundColor = '#F8F7F5';
            }
            table.appendChild(rowCell);
          });
        }

        widgetContainer.appendChild(table);

        if (!widget.resized) {
          this.stretchToFitContent(widget);
        }
      } catch (error) {
        if (this.user && this.user.user_type === "DEVELOPER") {
          console.log(error);
        }
      }

    },

    /**
     * Global function for generating tables
     *
     * @param   widget     Widget object
     * @param   data       Dataset for the stat boxes
     *
     * @return  void
     */
    renderStatBoxes(widget, data) {
      let stringLimit = 15;
      // Get container and set its height to 100%
      const widgetContainer = document.querySelector(`#${widget.id}`);
      if (widgetContainer) {
        widgetContainer.innerHTML = '';
      } else {
        console.log('Widget container no longer in dom');
        return;
      }
      widgetContainer.style.height = '100%';

      // Create wrapping element, apply class and height styling
      const wrapper = document.createElement('div');
      wrapper.classList.add('statbox_wrapper');
      wrapper.style.setProperty('height', '100%');
      _.forEach(data, row => {
        // Create the statbox element and add class
        const statbox = document.createElement('div');
        statbox.classList.add('statbox');

        // Create heading element and add class
        const statboxHeading = document.createElement('div');
        statboxHeading.classList.add('stabox_heading');

        // Create title element, add class and append to parent
        const statValue = document.createElement('div');
        statValue.classList.add('statbox_title');

        // Check if its a comparison stat box
        if (row['comp_value'] === null) {
          statValue.innerHTML = row['value'] ? row['value'].toLocaleString() : row['value'];
        } else {
          if (row['class']) statValue.classList.add(row['class']);

          if (row['difference'] === 0 && row['value'] > row['comp_value']) {
            statValue.innerHTML = `+${row['value']}`;
          } else {
            statValue.innerHTML = `${row['difference'] > 0 ? '+' : ''}${row['difference']}`;
          }
        }
        statboxHeading.appendChild(statValue);

        /**
         * TODO: This should only remain temporarily until all insights widgets have been removed from reports
         */
        if (widget.type === 'insights') {
          const tempError = document.createElement('i')
          tempError.classList.add('far', 'fa-exclamation-triangle', 'insights-error', 'tooltipster');
          tempError.title = 'Insights data is no longer available from Google. Please replace this widget with a "Performance" widget';
          statboxHeading.appendChild(tempError);
        }

        // Add Source if required, add class and append to parent
        if (row['source']) {
          const statSource = document.createElement('div');
          statSource.classList.add('statbox_source');
          statSource.innerHTML = `Source<br>${row['source']}`
          statboxHeading.appendChild(statSource);
        }
        // Append heading to statbox
        statbox.appendChild(statboxHeading);

        // Create statbox body, add class, html and append to statbox
        const statboxBody = document.createElement('div');
        statboxBody.classList.add('p_body', 'statbox_body');
        statboxBody.innerHTML = (row['title'] && row['title'].length > stringLimit) ? `${row['title'].substring(0, stringLimit)}...` : row['title'];
        if (row['title'].length > stringLimit) {
          statboxBody.classList.add('tooltipster');
          statboxBody.setAttribute('title', row['title']);
        }
        statbox.appendChild(statboxBody);

        // Add Detail if required, add class and append to parent
        if (row['details']) {
          const statDetail = document.createElement('span');
          statDetail.classList.add('statbox_detail');
          statDetail.innerHTML = row['details'];
          statbox.appendChild(statDetail);
        }

        /*
          TODO: Why is this not used?
          let compRangeText = '';
          if (widget.attributes.options.useDatesFrom == 'report') {
            compRangeText = this.compareRanges[this.report.comp_range];
          } else {
            compRangeText = this.compareRanges[widget.attributes.options.dates.compRange];
          }
        */

        if (row['comp_value'] !== null) {
          const statDiff = document.createElement('div');
          statDiff.classList.add('statbox_diff');
          if (row['class']) statDiff.classList.add(row['class']);
          statDiff.innerHTML = `<strong>${row['value']}</strong> vs ${row['comp_value']}`;

          statbox.appendChild(statDiff);
        }

        // Append it all to the wrapper
        wrapper.appendChild(statbox);

      });

      widgetContainer.appendChild(wrapper);

      if (!widget.resized) {
        this.stretchToFitContent(widget);
      }
    },

    /**
     * Global function for generating error messages in wigets
     *
     * @param   widget     Widget object
     * @param   error      Error response
     *
     * @return  void
     */
    renderError(widget, error) {
      if (this.user && this.user.user_type === "DEVELOPER") {
        console.log(error);
      }
      // Get container and set its height to 100%
      const widgetContainer = document.querySelector(`#${widget.id}`);
      if (widgetContainer) {
        widgetContainer.innerHTML = '';
      } else {
        console.log('Widget container no longer in dom');
        return;
      }
      widgetContainer.style.height = '100%';

      const errorWidget = document.createElement('div');
      errorWidget.classList.add('error_message');
      errorWidget.style.setProperty('height', '100%');

      const errorTitle = document.createElement('p');
      const errorContact = document.createElement('p');

      errorTitle.innerHTML = 'An error occurred whilst generating this widget';
      errorContact.innerHTML = 'Please contact our support team for assistance';

      const {error: errorMessage} = error;
      if (errorMessage && errorMessage.includes("The request's dimensions & metrics are incompatible")) {
        errorTitle.innerHTML = "Due to changes in Google's API, the current configuration of this widget is no longer " +
          "supported. We can no longer apply filters for the selected dimension";
        errorContact.innerHTML = 'Please edit this widget and clear the applied filters, or remove this widget from ' +
          'your report';
      }

      errorWidget.appendChild(errorTitle);
      errorWidget.appendChild(errorContact);
      widgetContainer.appendChild(errorWidget);
    },

    /**
     * Takes in a widget object and a boolean determining if we are refreshing
     *
     * @param   widget  Widget object
     * @param   refresh Boolean confirming if we are refreshing a widget
     *
     * @return  void
     */
    async generateWidget(widget, refresh = false) {
      // Declare widget types to be ignored
      let ignore = ['heading', 'textarea', 'image'];
      if (ignore.includes(widget.type)) {
        if (!widget.resized) this.stretchToFitContent(widget);
        return;
      }

      if (widget.type === 'ai-summary') {
        this.renderAiSummaryWidget(widget);
        return;
      }

      if (widget.type === 'table') {
        let data = {
          headers: widget.attributes.headers,
          data: widget.attributes.rows
        };
        this.renderTable(widget, data);
        return;
      }

      // Prep data object for request
      const data = await this.prepRequestData(widget);
      let axiosRequest = axios;
      // Check if the widget has been frozen
      if (widget.frozen) {
        // If so, render from the stored data.
        this.renderWidget(widget, widget.data);
      } else {
        if (refresh || this.preview || !widget.data || widget.data.chart) { // Added chart check to prevent errors with existing widgets. This will cause any widgets with old data format to be refreshed
          axiosRequest.post(`/ajax/reports/widgets/get-data`, {
            data: data
          })
            .then(response => {
              // If its not for a template, store ga and gsc data in the widget to save on duplicate requests
              if (!this.report.is_template) {
                widget.data = response.data;
                this.updateWidget({pageId: this.page.id, widget: widget, preview: this.preview});
              } else {
                // Delete the data from the widget if its a template
                widget.data = null;
                this.updateWidget({pageId: this.page.id, widget: widget, preview: this.preview});
              }
              this.renderWidget(widget, response.data);

            })
            .catch(error => {
              if (widget.data) {
                widget.data = null;
                let el = document.querySelector(`[gs-id=${widget.id}]`);
                this.grid.update(el, widget);
                if (!this.preview) {
                  this.savePages();
                }
              }

              if (error.response) {
                this.renderError(widget, error.response.data);
              } else {
                this.renderError(widget, error);
              }
            });
        } else {

          if (widget.type === 'ga' || widget.type === 'gsc') {
            this.renderWidget(widget, widget.data);
          } else {
            axiosRequest.post(`/ajax/reports/widgets/get-data`, {
              data: data
            })
              .then(response => {
                widget.data = response.data;
                this.renderWidget(widget, response.data);
              })
              .catch(error => {
                if (widget.data) {
                  widget.data = null;
                  let el = document.querySelector(`[gs-id=${widget.id}]`);
                  this.grid.update(el, widget);
                  if (!this.preview) {
                    this.savePages();
                  }
                }

                if (error.response) {
                  this.renderError(widget, error.response.data);
                } else {
                  this.renderError(widget, error);
                }
              });
          }
        }
      }

    },

    /**
     * Takes in a widget object and triggers the correct rendering function
     *
     * @param   widget  Widget object
     *
     * @return  void
     */
    renderWidget(widget, data) {
      if (widget.attributes.type.includes('chart')) {
        this.renderChart(widget, data.original.options);
      } else if (widget.attributes.type === 'stat-boxes') {
        this.renderStatBoxes(widget, data);
      } else if (widget.attributes.type === 'table') {
        this.renderTable(widget, data);
      }
    },

    /**
     * Prepares the data object for the request, specifically setting the date ranges
     *
     * @param   widget  Widget object
     *
     * @return  array   Returns data object to be passed as part of an axios request for widget data
     */
    async prepRequestData(widget) {
      // Declare date ranges using report data
      let date_range = this.report.date_range;
      let from_date = this.report.from_date;
      let to_date = this.report.to_date;
      let comp_range = this.report.comp_range;
      let comp_from_date = this.report.comp_from_date;
      let comp_to_date = this.report.comp_to_date;

      // Update date ranges if using a custom date range for the widget
      if (widget.attributes.options.useDatesFrom === 'custom') {
        date_range = widget.attributes.options.dates.dateRange;
        from_date = widget.attributes.options.dates.fromDate;
        to_date = widget.attributes.options.dates.toDate;
        comp_range = widget.attributes.options.dates.compRange;
        comp_from_date = widget.attributes.options.dates.compFromDate;
        comp_to_date = widget.attributes.options.dates.compToDate;
      }
      // Prep data object for request
      const data = {
        uuid: this.report.uuid,
        is_template: this.report.is_template,
        data_type: widget.type,
        date_range: date_range,
        from_date: from_date,
        to_date: to_date,
        comp_range: comp_range,
        comp_from_date: comp_from_date,
        comp_to_date: comp_to_date,
        widget_type: widget.attributes.type,
        options: widget.attributes.options,
        chart_options: widget.attributes.chartOptions
      };

      return data;
    },

    /**
     * Refreshes the whole Gridstack grid and all widgets within.
     *
     * @return  void
     */
    refreshGrid(referrer = null) {
      let widgets = this.grid.save();
      _.each(widgets, (widget) => {
        if (widget.attributes.type &&
          (
            widget.attributes.type.includes('chart') ||
            widget.attributes.type === 'stat-boxes' ||
            widget.attributes.type === 'table'
          )
        ) {
          widget.content = `<div id="${widget.id}"><div class="widget-loading"><i class="fa fa-btn fa-spinner fa-spin"></i></div></div>`

          if (referrer === 'comp_range' && widget.type === 'ga' && widget.attributes.type === 'table') {
            if (this.report.comp_range) {
              widget.attributes.options.visibleColumns = ['dimension', 'value', 'comp_value', 'difference'];
            } else {
              widget.attributes.options.visibleColumns = ['dimension', 'value'];
            }
          }

          let el = document.querySelector(`[gs-id=${widget.id}]`);
          this.grid.update(el, widget);
        }

        this.generateWidget(widget, true);
      });

      if (!this.preview) this.savePages();
    },

    isOverflownX(element) {
      return element.scrollWidth > element.clientWidth;
    },
    isOverflownY(element) {
      return element.scrollHeight > element.clientHeight;
    },
    stretchToFitContent(widget) {
      const widgetEl = document.querySelector(`[gs-id="${widget.id}"]`);
      const widgetContent = document.querySelector(`[gs-id="${widget.id}"] .grid-stack-item-content`);
      if (this.isOverflownY(widgetContent)) {
        const minRows = Math.ceil(widgetContent.scrollHeight / this.grid.getCellHeight()) + 1;
        this.grid.update(widgetEl, {h: minRows, resized: true});
        if (!this.preview) this.savePages();
      }
    },

    renderAiSummaryWidget(widget) {
      const widgetContainer = document.querySelector(`#${widget.id}`);
      if (!widgetContainer) {
        // Widget container no longer in the DOM
        return;
      }
      widgetContainer.innerHTML = '';
      const wrapper = document.createElement('div');
      wrapper.innerHTML = widget.content;
      widgetContainer.appendChild(wrapper);
    }
  }
}
</script>

<style scoped>
.grid-stack {
  background: white;
}
</style>
